import { useRef, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import ArtistCard from "@/components/lineup/ArtistCard";
import LineupSheet from "@/components/lineup/LineupSheet";
import SponsorsBlock from "@/components/SponsorsBlock";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView, useMobileOnceViewport } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import { buildMusicEventSchema } from "@/lib/event-schema";
import type { Artist } from "@/components/lineup/ArtistCard";

const fridayArtists: Artist[] = [
  {
    name: "Yacht Rock Revue",
    role: "The World's Premier Soft-Rock Party Band",
    image: "/artists/yacht-rock-revue.jpg",
    revealed: true,
    day: "Friday",
    time: "9:30 – 11:00 PM",
    stage: "Main Stage",
    bio: "Hailed by Rolling Stone as the 'world's premier soft-rock party band,' Yacht Rock Revue delivers dy-no-mite renditions of hits by Kenny Loggins, Michael McDonald, Steely Dan, and Christopher Cross. Widely credited with driving the yacht rock resurgence, 2026 marks their most ambitious production yet — it's PRIMETIME.",
    topSongs: ["Escape Artist", "Step", "Tropical Illusion"],
  },
  {
    name: "Pablo Cruise",
    role: "Yacht Rock Legends",
    image: "/artists/pablo-cruise.jpg",
    revealed: true,
    day: "Friday",
    time: "7:30 – 9:00 PM",
    stage: "Main Stage",
    bio: "Pablo Cruise defined the California yacht rock sound with hits like 'Love Will Find a Way' and 'Whatcha Gonna Do?' Now they're performing live in Detroit at The Roostertail, bringing warm Pacific grooves to the Detroit River.",
    topSongs: ["Love Will Find a Way", "Whatcha Gonna Do?", "A Place in the Sun"],
  },
  {
    name: "Seven Wonders",
    role: "Fleetwood Mac Tribute",
    image: "/artists/seven-wonders.jpg",
    revealed: true,
    day: "Friday",
    time: "5:30 – 7:00 PM",
    stage: "Main Stage",
    bio: "Seven Wonders is the premier Fleetwood Mac tribute band, faithfully recreating the magic of 'Go Your Own Way,' 'Dreams,' and 'The Chain.' They open Yacht Rock Detroit 2026 on Friday night at The Roostertail in Detroit.",
    topSongs: ["Go Your Own Way", "Dreams", "The Chain"],
  },
];

// Saturday is billed in tiers: national headliners first (Three Dog Night +
// the Ambrosia bill), then DJs. The Ambrosia package — Peter Beckett, Walter
// Egan, Elliot Lurie & John Ford Coley — is presented as ONE billing under the
// Ambrosia banner, not as five co-equal standalone acts.
const saturdayHeadliners: Artist[] = [
  {
    name: "Three Dog Night",
    role: "Saturday Headliner",
    image: "/artists/three-dog-night.jpg",
    revealed: true,
    day: "Saturday",
    time: "9:00 – 11:00 PM",
    stage: "Main Stage",
    bio: "Three Dog Night delivered era-defining hits like 'One,' 'Joy to the World,' and 'Mama Told Me Not to Come.' They headline Saturday night at Yacht Rock Detroit 2026, performing live at The Roostertail on the Detroit waterfront.",
    topSongs: ["One", "Joy to the World", "Mama Told Me Not to Come"],
  },
  {
    name: "Ambrosia",
    role: "featuring Peter Beckett, Walter Egan, Elliot Lurie & John Ford Coley",
    image: "/artists/ambrosia.jpg",
    revealed: true,
    day: "Saturday",
    time: "6:30 – 8:30 PM",
    stage: "Main Stage",
    bio: "Ambrosia delivered some of the smoothest yacht rock hits of the late '70s and early '80s, including 'Biggest Part of Me' and 'How Much I Feel.' On the Saturday bill they're joined by Peter Beckett, Walter Egan, Elliot Lurie, and John Ford Coley — live at The Roostertail on the Detroit waterfront.",
    topSongs: ["Biggest Part of Me", "How Much I Feel", "You're the Only Woman"],
  },
];

const ambrosiaBill: Artist[] = [
  {
    name: "Peter Beckett",
    role: 'Player — "Baby Come Back"',
    image: "/artists/peter-beckett.jpg",
    revealed: true,
    day: "Saturday",
    time: "6:30 – 8:30 PM",
    stage: "Main Stage",
    bio: "Peter Beckett is the voice behind Player's 'Baby Come Back' — the #1 hit that became a yacht rock standard. He performs live at Yacht Rock Detroit 2026 on the Detroit waterfront at The Roostertail, July 17-18.",
    topSongs: ["Baby Come Back", "This Time I'm in It for Love", "Prisoner of Your Love"],
  },
  {
    name: "Walter Egan",
    role: '"Magnet and Steel"',
    image: "/artists/walter-egan.jpg",
    revealed: true,
    day: "Saturday",
    time: "6:30 – 8:30 PM",
    stage: "Main Stage",
    bio: "Walter Egan crafted 'Magnet and Steel' with Fleetwood Mac's Stevie Nicks and Lindsey Buckingham — a yacht rock essential. He performs live on the Detroit River at Yacht Rock Detroit 2026 at The Roostertail.",
    topSongs: ["Magnet and Steel", "Hot Summer Nights", "Only the Lucky"],
  },
  {
    name: "Elliot Lurie",
    role: 'Looking Glass — "Brandy"',
    image: "/artists/elliot-lurie.jpg",
    revealed: true,
    day: "Saturday",
    time: "6:30 – 8:30 PM",
    stage: "Main Stage",
    bio: "Elliot Lurie, frontman of Looking Glass, wrote and sang 'Brandy (You're a Fine Girl)' — one of the defining yacht rock anthems of the 1970s. He brings that timeless hit to Detroit's waterfront stage at The Roostertail for Yacht Rock Detroit 2026.",
    topSongs: ["Brandy (You're a Fine Girl)", "Jimmy Loves Mary-Anne", "Golden Rainbow"],
  },
  {
    name: "John Ford Coley",
    role: '"I\'d Really Love to See You Tonight"',
    image: "/artists/john-ford-coley.jpg",
    revealed: true,
    day: "Saturday",
    time: "6:30 – 8:30 PM",
    stage: "Main Stage",
    bio: "John Ford Coley, of England Dan & John Ford Coley, is best known for 'I'd Really Love to See You Tonight' and 'Nights Are Forever Without You.' He takes the stage in Detroit at The Roostertail as part of the Yacht Rock Detroit 2026 lineup.",
    topSongs: ["I'd Really Love to See You Tonight", "Nights Are Forever Without You", "Love Is the Answer"],
  },
];

const partyPupils: Artist = {
  name: "Party Pupils",
  role: "Yacht House DJ Set",
  image: "/pp-photo.webp",
  imageMobile: "/pp-photo-sm.webp",
  logoUrl: "/pp-logo.webp",
  accentColor: "#A8FF2F",
  revealed: true,
  day: "Saturday",
  time: "9:30 – 11:30 PM",
  stage: "The Deck",
  bio: "Party Pupils are the duo who built yacht house. They remixed Christopher Cross's 'Ride Like the Wind' and dropped 'Yacht House Summer Vol 1' in March 2026. Saturday on the Detroit River.",
  topSongs: ["Yacht House Summer Vol 1", "Ride Like the Wind (Christopher Cross Remix)", "Love Me for the Weekend"],
};

/** Auxiliary acts — every performer from the event timeout beyond the Main
 *  Stage bill. Icons are small square crops in /public/artists/icons/,
 *  sourced from the artist-upload form (Firestore artist_uploads). */
type SupportAct = {
  name: string;
  role?: string;
  sets: string;
  icon?: string;
};

const deckDjs: SupportAct[] = [
  { name: "DJ Daryl G", sets: "Fri 4 – 6 PM", icon: "/artists/icons/dj-daryl-g.jpg" },
  { name: "Mike R.", sets: "Fri 6 – 7:45 PM", icon: "/artists/icons/mike-r.jpg" },
  { name: "DJ Manuel Antonio", sets: "Fri 7:45 – 9:45 PM", icon: "/artists/icons/dj-manuel-antonio.jpg" },
  { name: "Mr Wired Up", sets: "Fri 9:45 – 11:45 PM · Sat 3:30 – 5:30 PM", icon: "/artists/icons/mr-wired-up.jpg" },
  { name: "DJ Rick Scampi", sets: "Sat Noon – 1:30 PM", icon: "/artists/icons/dj-rick-scampi.jpg" },
  { name: "DJ Charlie", sets: "Sat 1:30 – 3:30 PM · plus Admirals Lounge 7:45 – 9:45 PM", icon: "/artists/icons/dj-charlie.jpg" },
  { name: "DiscJockey George", sets: "Sat 5:30 – 7:45 PM", icon: "/artists/icons/discjockey-george.jpg" },
  { name: "DJ Ric Nasty", sets: "Sat 7:45 – 9:45 PM", icon: "/artists/icons/dj-ric-nasty.jpg" },
];

const supportActs: SupportAct[] = [
  {
    name: "Urban Tropical",
    role: "Tropical grooves",
    sets: "Sat Noon – 3 PM · Garden",
    icon: "/artists/icons/urban-tropical.jpg",
  },
  {
    name: "Yacht Rock Trivia",
    role: "hosted by Scott Zumberg — the smoothest trivia in town",
    sets: "Sat 12:30 – 3 PM · Marine Room",
    icon: "/artists/icons/yacht-rock-trivia.jpg",
  },
  {
    name: "Not 2 Nite",
    role: "Yacht rock party band",
    sets: "Fri 4:30 PM · Admirals Lounge — Sat 3:30 PM · Marine Room",
    icon: "/artists/icons/not-2-nite.jpg",
  },
  {
    name: "The Yacht-Seas",
    role: "Smooth-rock band",
    sets: "Fri 4 PM & 8:45 PM · Marine Room — Sat 3 PM · Admirals Lounge",
    icon: "/artists/icons/the-yacht-seas.jpg",
  },
  {
    name: "DJ TJ Monte",
    sets: "Fri 3 PM Garden & 6:30 PM Marine Room — Sat 3:30 PM Garden",
    icon: "/artists/icons/dj-tj-monte.jpg",
  },
  {
    name: "Problematic Black Hottie",
    role: "DJ set",
    sets: "Sat 6 – 8 PM · Marine Room",
    icon: "/artists/icons/problematic-black-hottie.jpg",
  },
  {
    name: "DJ WaltAmore",
    sets: "Sat 5:30 – 7:45 PM · Admirals Lounge",
    icon: "/artists/icons/dj-waltamore.jpg",
  },
  {
    name: "DJ B Free",
    sets: "Sat 8 – 10 PM · Marine Room",
    icon: "/artists/icons/dj-b-free.jpg",
  },
  {
    name: "Charles & Wyatt",
    role: "Dueling pianos & Yacht Rock Karaoke",
    sets: "Fri & Sat · Admirals Lounge",
    icon: "/artists/icons/charles-wyatt.jpg",
  },
  {
    name: "The Act",
    role: "Detroit classic rock & roll",
    sets: "Fri 10:30 PM · Admirals Lounge — Sat 10:30 PM · Marine Room",
    icon: "/artists/icons/the-act.jpg",
  },
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** Editorial roster row: small round icon (or initials disc), name, sets. */
const SupportRow = ({ act }: { act: SupportAct }) => (
  <div className="flex items-center gap-4 py-3.5 md:py-4 border-b border-yacht-charcoal/12">
    {act.icon ? (
      <img
        src={act.icon}
        alt={act.name}
        loading="lazy"
        width={56}
        height={56}
        className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover shrink-0"
      />
    ) : (
      <span className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-yacht-charcoal/[0.06] border border-yacht-charcoal/10 flex items-center justify-center shrink-0">
        <span className="text-yacht-charcoal/40 text-sm md:text-base font-bold tracking-wide">
          {initials(act.name)}
        </span>
      </span>
    )}
    <span className="min-w-0">
      <span className="block text-base md:text-lg font-bold text-yacht-charcoal leading-tight">
        {act.name}
        {act.role && (
          <span className="font-normal text-yacht-charcoal/50 text-sm md:text-base"> — {act.role}</span>
        )}
      </span>
      <span className="block text-xs md:text-sm text-yacht-charcoal/50 mt-0.5">
        {act.sets}
      </span>
    </span>
  </div>
);

const Lineup = () => {
  const lineupJsonLd = useMemo(() => buildMusicEventSchema({
    url: "https://yachtrockdetroit.com/lineup",
    description: "Full 2026 lineup for Yacht Rock Detroit — Detroit's waterfront music festival at The Roostertail, July 17-18. Friday: Yacht Rock Revue, Pablo Cruise, Seven Wonders. Saturday: Three Dog Night, Ambrosia, Elliot Lurie, Peter Beckett, Walter Egan, John Ford Coley. Live concerts on the Detroit River.",
  }), []);

  usePageSEO({
    title: "2026 Lineup — Yacht Rock Detroit | July 17-18 Concerts",
    description: "The full Yacht Rock Detroit 2026 lineup: Three Dog Night, Pablo Cruise, Ambrosia, Yacht Rock Revue & more, live at The Roostertail July 17-18.",
    keywords: "yacht rock detroit lineup, detroit concert lineup 2026, live music detroit waterfront, three dog night detroit, pablo cruise live detroit, detroit summer concerts 2026, upcoming concerts detroit, detroit music festival lineup, roostertail detroit concerts july 2026",
    ogImage: PHOTOS.STAGE.fullBandCaptainHats,
    jsonLd: lineupJsonLd,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Lineup", url: "https://yachtrockdetroit.com/lineup" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });
  const mobileOnceViewport = useMobileOnceViewport("-100px");

  const [selected, setSelected] = useState<Artist | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* ── Hero ── */}
      <section
        ref={headerRef}
        className="relative min-h-fit md:min-h-[60vh] bg-yacht-blue overflow-hidden flex flex-col justify-end"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: "brightness(0) invert(1)" }}
        />

        <div className="relative z-20 px-6 md:px-16 pt-28 pb-12 md:pt-36 md:pb-20">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            July 17–18, 2026 · The Roostertail
          </motion.p>
          <h1 className="text-[12vw] md:text-[8vw] lg:text-[6vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="The Lineup" duration={0.5} delayMultiple={0.06} />
          </h1>
          <motion.p
            className="text-white/60 text-base md:text-lg mt-4 max-w-lg"
            initial={{ opacity: 0 }}
            animate={isHeaderInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ fontFamily: "Georgia, serif" }}
          >
            The 2026 bill — a weekend, 25+ live acts, every smooth hit you know, live on the waterfront.
          </motion.p>
        </div>
      </section>

      {/* ── Yellow accent strip ── */}
      <section className="relative py-6 bg-yacht-yellow overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 px-6 md:px-16 text-center">
          <p className="text-yacht-charcoal/80 text-lg md:text-xl font-medium" style={{ fontFamily: "Georgia, serif" }}>
            Every yacht rock song you know — played live. That was the 2026 lineup.
          </p>
        </div>
      </section>


      {/* ── FRIDAY ── */}
      <section className="pt-20 md:pt-32 pb-8 px-6 md:px-16 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="text-[16vw] md:text-[12vw] lg:text-[10vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight mb-1 -ml-2 md:-ml-6"
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            FRIDAY
          </motion.h2>
          <motion.p
            className="text-yacht-blue text-3xl md:text-5xl lg:text-6xl font-bold mb-2 ml-8 md:ml-16 italic"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            July 17, 2026
          </motion.p>
          <motion.p
            className="text-muted-foreground text-base md:text-lg mb-10 md:mb-14 ml-8 md:ml-16 max-w-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.5, delay: 0.25 }}
            style={{ fontFamily: "Georgia, serif" }}
          >
            Yacht Rock Revue headlined Day One, with Pablo Cruise and Seven Wonders.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {fridayArtists.map((artist, i) => (
              <ArtistCard key={`fri-${i}`} artist={artist} index={i} onOpen={setSelected} />
            ))}
          </div>
        </div>
      </section>

      {/* ── SATURDAY ── */}
      <section className="pt-20 md:pt-32 pb-8 px-6 md:px-16 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="text-[16vw] md:text-[12vw] lg:text-[10vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight mb-1 text-right -mr-2 md:-mr-6"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            SATURDAY
          </motion.h2>
          <motion.p
            className="text-yacht-yellow text-3xl md:text-5xl lg:text-6xl font-bold mb-2 text-right mr-8 md:mr-16 italic"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            July 18, 2026
          </motion.p>
          <motion.p
            className="text-muted-foreground text-base md:text-lg mb-10 md:mb-14 text-right mr-8 md:mr-16 max-w-lg ml-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.5, delay: 0.25 }}
            style={{ fontFamily: "Georgia, serif" }}
          >
            Three Dog Night headlined the big Saturday bill, with the full Ambrosia lineup.
          </motion.p>

          {/* National headliners */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {saturdayHeadliners.map((artist, i) => (
              <ArtistCard key={`sat-hl-${i}`} artist={artist} index={i} onOpen={setSelected} />
            ))}
          </div>

          {/* The Ambrosia bill — billed together, under the Ambrosia banner */}
          <motion.div
            className="mt-12 md:mt-16 mb-6 md:mb-8 text-right"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.5 }}
          >
            <p className="text-yacht-yellow text-xs md:text-sm font-bold tracking-[0.3em] uppercase">
              Ambrosia featuring
            </p>
            <p className="text-yacht-charcoal/70 text-sm md:text-base mt-1" style={{ fontFamily: "Georgia, serif" }}>
              Peter Beckett · Walter Egan · Elliot Lurie · John Ford Coley
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {ambrosiaBill.map((artist, i) => (
              <ArtistCard key={`sat-bill-${i}`} artist={artist} index={i} onOpen={setSelected} />
            ))}
          </div>

        </div>
      </section>

      {/* ── HIGH NOON DECK PARTY ── */}
      <section className="pt-20 md:pt-32 pb-8 px-6 md:px-16 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="sr-only">High Noon Deck Party</h2>
          <img
            src="/high-noon-deck-party.png"
            alt="High Noon Deck Party"
            width={800}
            height={323}
            className="w-[78vw] max-w-[560px] h-auto mb-6 md:mb-8"
          />
          <p
            className="text-muted-foreground text-base md:text-lg mb-10 md:mb-14 ml-8 md:ml-16 max-w-lg"
            style={{ fontFamily: "Georgia, serif" }}
          >
            The High Noon Deck Party — a floating DJ deck on the Detroit
            River, nonstop DJs from doors to midnight, both days.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <ArtistCard artist={partyPupils} index={0} onOpen={setSelected} />
          </div>

          <div className="mt-8 md:mt-10 border-t-2 border-yacht-charcoal grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
            {deckDjs.map((act) => (
              <SupportRow key={act.name} act={act} />
            ))}
          </div>
        </div>
      </section>

      {/* ── ACROSS THE PROPERTY — auxiliary acts ── */}
      <section className="pt-20 md:pt-32 pb-8 px-6 md:px-16 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="text-[13vw] md:text-[9vw] lg:text-[7.5vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight mb-1 text-right -mr-1 md:-mr-3"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            ACROSS THE PROPERTY
          </motion.h2>
          <p
            className="text-muted-foreground text-base md:text-lg mb-10 md:mb-14 text-right mr-8 md:mr-16 max-w-lg ml-auto"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Bands, DJs, trivia and karaoke — in the Garden, the Marine Room,
            and the Admirals Lounge.
          </p>

          <div className="border-t-2 border-yacht-charcoal grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
            {supportActs.map((act) => (
              <SupportRow key={act.name} act={act} />
            ))}
          </div>

          <p className="text-yacht-charcoal/45 text-xs md:text-sm mt-6 leading-relaxed">
            Full set times for every act are on the{" "}
            <a href="/schedule" className="underline font-semibold text-yacht-charcoal/70">
              Schedule
            </a>
            . Admirals Lounge was open to VIP ticket holders.
          </p>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-20 md:py-28 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <a
            href="/tickets"
            className="inline-block bg-yacht-yellow text-yacht-charcoal text-sm md:text-base font-bold tracking-widest uppercase px-8 py-4 rounded-full shadow-lg hover:brightness-110 transition-all"
          >
            Get 2027 Early Access →
          </a>
          <p className="text-muted-foreground text-sm mt-4 max-w-md mx-auto">
            What a weekend. Sign up to get 2027 early access — dates, lineup, and tickets before the public.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto mt-16 md:mt-20">
          <SponsorsBlock />
        </div>
      </section>

      {selected && <LineupSheet artist={selected} onClose={() => setSelected(null)} />}

      <Footer />
    </div>
  );
};

export default Lineup;
